import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';

export const styleSheet = StyleSheet.create({
	root: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) auto',
		height: '100%',
		width: '100%',
	},
	// The board's tiles and tables need the full width, not the light page's centered 1400px column.
	fullWidthPage: {
		maxWidth: 'none',
		padding: `${SPACE[24]}px ${SPACE[16]}px`,
	},
	// Scrolls on its own so the side drawer beside it stays pinned to the viewport.
	pageColumn: {
		height: '100%',
		minWidth: 0,
		overflowY: 'auto',
	},
	panel: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		minHeight: 0,
		overflow: 'hidden',
	},
	// Opening a call hides the board rather than unmounting it, so its filters survive the round trip.
	boardPaneHidden: {
		display: 'none',
	},
	// Shown in place of the detail panels when a `?convo=<id>` deep link resolves to nothing (404).
	detailNotFound: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		justifyContent: 'center',
		padding: THEME.spacing.xl,
		textAlign: 'center',
	},
	panelBody: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flex: 1,
		fontSize: THEME.typography.fontSizes.sm,
		justifyContent: 'center',
		minHeight: 0,
		padding: THEME.spacing.lg,
		textAlign: 'center',
	},
	detailBody: {
		// Let the transcript flow at its natural height so the whole detail column scrolls as one
		// (the parent `detailPanel` owns the scrollbar). NOT flex:1 — otherwise the transcript becomes
		// a bounded internal scroll window and squeezes the rest of the column off-screen (the bug
		// where the right pane couldn't be scrolled).
		display: 'flex',
	},
	thread: {
		backgroundColor: THEME.colors.background.light,
		flex: 1,
		paddingLeft: THEME.spacing.lg,
		[THEME.queries.md]: {
			paddingLeft: THEME.spacing.md,
		},
	},
	threadContent: {
		paddingBottom: THEME.spacing.lg,
		paddingRight: THEME.spacing.lg,
		paddingTop: THEME.spacing.md,
		[THEME.queries.md]: {
			paddingRight: THEME.spacing.md,
		},
	},
	spinnerState: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		justifyContent: 'center',
		minHeight: 240,
	},
	adherence: {
		borderBottom: THEME.common.border,
		flexShrink: 0,
	},
	adherenceBody: {
		padding: `${THEME.space[16]}px ${THEME.space[24]}px`,
	},
	adherenceEmpty: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.sm,
	},
	metadata: {
		borderBottom: THEME.common.border,
		flexShrink: 0,
	},
	metadataHeader: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
	},
	metadataTitle: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	metadataBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
		margin: 0,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	metaTop: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
	},
	// Sizes the shared Avatar down from its 60px default to fit the metadata header.
	metaAvatar: {
		flexShrink: 0,
		height: 36,
		width: 36,
	},
	metaIdentity: {
		alignItems: 'flex-start',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		minWidth: 0,
	},
	metaRep: {
		...THEME.common.truncate,
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		maxWidth: '100%',
	},
	metaFeedback: {
		display: 'flex',
		gap: THEME.spacing.sm,
		marginLeft: 'auto',
	},
	fbChip: {
		alignItems: 'center',
		borderRadius: 9999,
		display: 'inline-flex',
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.semibold,
		gap: THEME.spacing.xs,
		padding: `2px ${THEME.spacing.sm}px`,
	},
	fbChipUp: {
		backgroundColor: THEME.colors.pill.green.background,
		color: THEME.colors.pill.green.icon,
	},
	fbChipDown: {
		backgroundColor: THEME.colors.pill.red.background,
		color: THEME.colors.pill.red.icon,
	},
	metaStats: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		margin: 0,
	},
	metaStat: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		flex: '1 1 130px',
		minWidth: 120,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	metaStatLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
	},
	metaStatValue: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.sm,
		lineHeight: 1.3,
		margin: 0,
		marginTop: THEME.spacing.xs,
	},
	metaIdRow: {
		alignItems: 'center',
		borderTop: `1px solid ${THEME.colors.border.light}`,
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		paddingTop: THEME.spacing.md,
	},
	// Sized to its content (not flex: 1) so the id chip hugs the value instead of filling the row.
	metaIdValue: {
		...THEME.common.truncate,
		backgroundColor: THEME.colors.background.main,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		color: THEME.colors.text.main,
		flexShrink: 1,
		fontFamily: 'monospace',
		fontSize: THEME.typography.fontSizes.xs,
		maxWidth: '100%',
		minWidth: 0,
		padding: `3px ${THEME.spacing.sm}px`,
	},
	metaIdCopy: {
		flexShrink: 0,
		gap: THEME.spacing.xs,
		marginLeft: 'auto',
	},
	metaIdCopyIcon: {
		height: 14,
		width: 14,
	},
	insights: {
		borderBottom: THEME.common.border,
		flexShrink: 0,
	},
	insightsHeader: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'space-between',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.lg}px`,
	},
	insightsTitle: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		margin: 0,
	},
	insightsBody: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.lg,
		padding: `${THEME.spacing.md}px ${THEME.spacing.lg}px`,
	},
	insightsChartTitle: {
		color: THEME.colors.text.main,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.semibold,
		margin: 0,
		marginBottom: THEME.spacing.sm,
	},
	// Collapsible trigger chevron — points down when collapsed, flips up (180deg) when open.
	collapseChevron: {
		color: THEME.colors.text.light,
		transition: 'transform 0.15s ease-out',
	},
	collapseChevronOpen: {
		transform: 'rotate(180deg)',
	},
	insightsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	// Compact, content-sized stat chips for the Insights summary (short values like "15s", "3").
	insightsStats: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: THEME.spacing.sm,
		margin: 0,
	},
	insightsStat: {
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		flex: '0 1 auto',
		padding: `${THEME.spacing.xs}px ${THEME.spacing.sm}px`,
	},
	insightsStatLabel: {
		color: THEME.colors.text.light,
		fontSize: THEME.typography.fontSizes.xxs,
		fontWeight: THEME.typography.fontWeights.bold,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
	},
	insightsStatValue: {
		color: THEME.colors.text.dark,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		lineHeight: 1.2,
		margin: 0,
		marginTop: 2,
	},
	insightsChartSuffix: {
		color: THEME.colors.text.light,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	turnGapChart: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	turnGapRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.md,
	},
	turnGapLabel: {
		color: THEME.colors.text.light,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		lineHeight: 1.2,
		textAlign: 'right',
		width: 132,
	},
	turnGapTrack: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.main,
		borderRadius: THEME.common.borderRadius,
		display: 'flex',
		flex: 1,
		height: 24,
		minWidth: 0,
		overflow: 'visible',
	},
	turnGapBar: {
		borderRadius: THEME.common.borderRadius,
		flexShrink: 0,
		height: 24,
		minWidth: 6,
		transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
	},
	turnGapValue: {
		color: THEME.colors.text.main,
		flexShrink: 0,
		fontSize: THEME.typography.fontSizes.xs,
		fontWeight: THEME.typography.fontWeights.bold,
		marginLeft: THEME.spacing.sm,
		whiteSpace: 'nowrap',
	},
	insightsSpinner: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'center',
		minHeight: 80,
	},
	insightsTooltip: {
		backgroundColor: THEME.colors.background.light,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		boxShadow: `0 2px 8px ${THEME.colors.border.main}`,
		fontSize: THEME.typography.fontSizes.xs,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	insightsTooltipTitle: {
		color: THEME.colors.text.dark,
		fontWeight: THEME.typography.fontWeights.bold,
		marginBottom: THEME.spacing.xs,
	},
	insightsTooltipLine: {
		color: THEME.colors.text.main,
		margin: 0,
	},
});
